import { useEffect, useState } from 'react';
import { useAuth0User } from '@hooks/useAuth0User';
import Button from '@components/atoms/button';
import Icon from '@components/atoms/icon';
import { BodyRegular, BodyRegularBold, H1 } from '@components/atoms/typography';
import {
  MOBILE_COLLAPSED_LIST_ITEMS,
  MOBILE_EXPANDED_LIST_ITEMS
} from '@lib/page-utils/Join';
import {
  CleverTap,
  ClevertapEvents,
  ClevertapEventProperties
} from '@lib/clevertap';
import {
  getCheckoutPath,
  getCleverTapMembershipTier,
  getCleverTapTierName,
  isPaidMember
} from '@lib/utils';
import { PLAN_NAMES } from '@lib/constants';
import {
  AnalyticsEvents,
  captureAnalyticsEvent,
  toAnalyticsMembershipTier
} from '@lib/analytics/events';
import AnimateSpinner from '@components/animate-spinner';

// FFH-2372: analytics placement for the join-page plan card CTA.
const JOIN_CARD_PLACEMENT = 'join_card';

type JoinCardProps = {
  title: string;
  subHeader?: string;
  discountedAnnualPerMonthPrice?: string;
  priceId?: number;
  fullAnnualPerMonthPrice?: string;
  listItems: string[];
  userMembershipIds?: number[];
  variant: 'member-upgrade' | 'join';
  plansAreLoading: boolean;
  showFullPriceOnly?: boolean;
};

const JoinCard = ({
  title,
  subHeader,
  discountedAnnualPerMonthPrice,
  priceId,
  listItems,
  fullAnnualPerMonthPrice,
  userMembershipIds,
  variant,
  plansAreLoading,
  showFullPriceOnly = false
}: JoinCardProps) => {
  const [displayAllListItems, setDisplayAllListItems] = useState(false);
  const [referralId, setReferralId] = useState('');
  const { user } = useAuth0User();

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.Rewardful?.referral) {
      if (referralId !== window.Rewardful.referral) {
        setReferralId(window.Rewardful.referral);
      }
    }
  }, []);

  const hideCTAButton = priceId && userMembershipIds?.includes(priceId);

  const getPlanKey = (title) => {
    const planMap = {
      Pro: 'pro',
      Ultra: 'ultra',
      Starter: 'starter',
      Free: 'free'
    };
    return planMap[title] || 'free';
  };

  const planKey = getPlanKey(title);
  const listToDisplay = displayAllListItems
    ? MOBILE_EXPANDED_LIST_ITEMS[planKey] || listItems
    : MOBILE_COLLAPSED_LIST_ITEMS[planKey];

  return (
    <div className="p-4 rounded-lg bg-primary-100 space-y-3">
      {title === 'Pro' && (
        <div className="bg-warning-600 rounded-lg p-2 inline-flex">
          <BodyRegular className="text-white">Most Popular</BodyRegular>
        </div>
      )}
      <BodyRegularBold>{title}</BodyRegularBold>
      <BodyRegular>{subHeader}</BodyRegular>
      {/* Struck-through full price, only while a discount is applied: with a
          0% effective discount both prices are the same string, so the
          strike-through would just duplicate the price. */}
      {!plansAreLoading &&
        !showFullPriceOnly &&
        fullAnnualPerMonthPrice &&
        fullAnnualPerMonthPrice !== discountedAnnualPerMonthPrice && (
          <h2 className="my-3 mb-1 text-neutral-600 line-through font-normal text-4xl font-sans">
            {fullAnnualPerMonthPrice === 'Unavailable'
              ? 'Unavailable'
              : `${fullAnnualPerMonthPrice}/mth`}
          </h2>
        )}
      {!plansAreLoading &&
        discountedAnnualPerMonthPrice &&
        !showFullPriceOnly && (
          <H1 className="text-primary-800">
            {discountedAnnualPerMonthPrice === 'Unavailable'
              ? 'Unavailable'
              : `${discountedAnnualPerMonthPrice}/mth`}
          </H1>
        )}
      {!plansAreLoading && fullAnnualPerMonthPrice && showFullPriceOnly && (
        <H1 className="text-primary-800">
          {fullAnnualPerMonthPrice === 'Unavailable'
            ? 'Unavailable'
            : `${fullAnnualPerMonthPrice}/mth`}
        </H1>
      )}
      {plansAreLoading && <AnimateSpinner customClasses="h-5 w-5" />}
      {discountedAnnualPerMonthPrice && !showFullPriceOnly && (
        <BodyRegular className="text-primary-800 mb-2">
          Billed Yearly
        </BodyRegular>
      )}
      {fullAnnualPerMonthPrice && showFullPriceOnly && (
        <BodyRegular className="mb-2">Billed Yearly</BodyRegular>
      )}
      {(title === PLAN_NAMES.PRO || title === PLAN_NAMES.ULTRA) && (
        <BodyRegular className="mb-3">
          All {title === PLAN_NAMES.PRO ? PLAN_NAMES.STARTER : PLAN_NAMES.PRO}{' '}
          features PLUS:
        </BodyRegular>
      )}
      <ul>
        {listToDisplay.map((item) => (
          <li key={item} className="flex mb-3">
            <div>
              {' '}
              <Icon
                className="text-primary rounded-full w-6 h-6"
                name="CheckCircle"
              />
            </div>
            <BodyRegular className="ml-2">{item}</BodyRegular>
          </li>
        ))}
      </ul>
      {title === 'Free plan' && (
        <Button
          href="/auth/login?screen_hint=signup&returnTo=/welcome"
          className="w-full"
          variant="primary"
        >
          Start for free
        </Button>
      )}
      {hideCTAButton ? (
        <div>
          <p
            className="text-base font-bold text-center text-gray-500"
            data-cy={`join-current-plan-${title.toLowerCase()}`}
          >
            You already have this plan
          </p>
        </div>
      ) : (
        title !== 'Free plan' && (
          <div>
            <Button
              href={getCheckoutPath(user, priceId)}
              isLink
              className="w-full"
              variant="primary"
              dataCy={`join-button-text-${title.toLowerCase()}`}
              onClick={() => {
                // checkout_started: clear purchase intent, fired before
                // Stripe takes over (FFH-1049).
                CleverTap.event(ClevertapEvents.checkoutStarted, {
                  [ClevertapEventProperties.intendedTier]:
                    getCleverTapTierName(title),
                  [ClevertapEventProperties.membershipTier]:
                    getCleverTapMembershipTier(user)
                });
                // FFH-991 (Option A): Meta/TikTok AddToCart moved off this CTA
                // to the checkout-page mount, firing via the GTM container off
                // the `add_to_cart` dataLayer push (deduped with the server
                // CAPI). The direct fbq/ttq calls were removed.
                // FFH-2372: PostHog alongside CleverTap, never instead. The
                // join cards sell yearly plans only, and no coupon is in
                // scope here — the checkout page resolves the real one.
                captureAnalyticsEvent(AnalyticsEvents.upgradeCtaClicked, {
                  placement: JOIN_CARD_PLACEMENT,
                  intended_tier: toAnalyticsMembershipTier(title)
                });
                captureAnalyticsEvent(AnalyticsEvents.checkoutStarted, {
                  intended_tier: toAnalyticsMembershipTier(title),
                  billing_period: 'annual',
                  coupon: null
                });
              }}
            >
              {variant === 'member-upgrade' || isPaidMember(user)
                ? 'Change Plan'
                : 'Get started'}
            </Button>
          </div>
        )
      )}
      <div>
        <div className="mt-6 flex items-center justify-center cursor-pointer">
          <BodyRegular
            onClick={() => setDisplayAllListItems(!displayAllListItems)}
          >
            {displayAllListItems ? 'Collapse all features' : 'See all features'}
          </BodyRegular>
          <Icon
            className={`ml-1 text-neutral-600 w-6 h-6 ${
              displayAllListItems ? 'rotate-180' : ''
            }`}
            name="ChevronDown"
          />
        </div>
      </div>
    </div>
  );
};

export default JoinCard;
